import { inject, Injectable } from "@angular/core";
import { DataService } from "./data.service";
import { Query, QueryObjectsModel } from "@barthauer/abc/core/data";
import { map, Observable } from "rxjs";

export interface VersionedEntity {
  ObjectType?: string;
  ParentId?: string;
  ValidFrom: string;
  ValidTo?: string | null;

  [key: string]: any;
}

export interface AuditRow {
  object?: string;
  objectId: string;
  eventType: string;
  objectDesignation?: string;
  property: string;
  user?: string;
  changeDate?: string;
  oldValue: any;
  newValue: any;
}

export interface AuditConfig {
  objectIdField: string;

  // Static or dynamic object type
  objectType?: string;
  objectTypeField?: string;

  designationField?: string;

  validFromField: string;
  validToField: string;

  insertUserField?: string;
  updateUserField?: string;
  insertDateField?: string;
  updateDateField?: string;

  ignoredFields?: string[];

  fieldLabels?: Record<string, string>;

  // 🔥 Reference mapping: Id field -> Name field
  referenceFields?: Record<string, string>;
}

@Injectable({
  providedIn: "root",
})
export class HistoryService {
  private dataService = inject(DataService);
  private translator = this.dataService.res;

  public getHistory<T extends VersionedEntity>(
    objectType: string,
    startDate: string,
    endDate: string,
    id: string,
    columns: any[],
    config: AuditConfig,
    useParentId: boolean = false,
  ): Observable<AuditRow[]> {
    const queryObject = new Query();
    queryObject.ObjectType = objectType;

    queryObject.OPath = useParentId
      ? `Type(${objectType},#${startDate}#,#${endDate}#)[ParentId = '${id}']`
      : `Type(${objectType},#${startDate}#,#${endDate}#)[Id = '${id}']`;

    queryObject.Columns = columns;

    const queryModel = new QueryObjectsModel();
    queryModel.ObjectQueries = [queryObject];

    return this.dataService.coreDataService
      .executeReadObjectsQuery(queryModel)
      .pipe(
        map((response) => {
          const rows = Array.isArray(response) ? response : [];
          return this.buildAuditHistory<T>(rows, config);
        }),
      );
  }

  // ============================================================
  // CORE AUDIT BUILDER
  // ============================================================

  public buildAuditHistory<T extends VersionedEntity>(
    rows: T[],
    config: AuditConfig,
  ): AuditRow[] {
    const audit: AuditRow[] = [];
    if (!rows?.length) return audit;

    const ignored = new Set([
      config.validFromField,
      config.validToField,
      config.insertUserField,
      config.updateUserField,
      config.insertDateField,
      config.updateDateField,
      "ObjectType",
      "ParentId",
      ...(config.ignoredFields ?? []),
    ]);

    // Group by object id (important if multiple objects returned)
    const grouped = rows.reduce(
      (acc, row) => {
        const id = row[config.objectIdField];
        acc[id] = acc[id] || [];
        acc[id].push(row);
        return acc;
      },
      {} as Record<string, T[]>,
    );

    Object.values(grouped).forEach((versions) => {
      // Sort by ValidFrom ascending
      versions.sort(
        (a, b) =>
          new Date(a[config.validFromField]).getTime() -
          new Date(b[config.validFromField]).getTime(),
      );

      versions.forEach((current, index) => {
        const previous = versions[index - 1];
        const isLastVersion = index === versions.length - 1;
        const isDeleted = isLastVersion && !!current[config.validToField];

        const objectType =
          config.objectType ??
          (config.objectTypeField
            ? current[config.objectTypeField]
            : undefined);

        const objectId = current[config.objectIdField];
        const designation = config.designationField
          ? current[config.designationField]
          : undefined;

        // ==========================
        // CREATE
        // ==========================
        if (!previous) {
          audit.push({
            object: objectType,
            objectId,
            eventType: this.translator("Audit-ChangeType-Add"),
            objectDesignation: designation,
            property: "-",
            user: current[config.insertUserField ?? ""],
            changeDate: current[config.insertDateField ?? ""],
            oldValue: null,
            newValue: this.translator("Audit-ChangeType-Add"),
          });
          return;
        }

        // ==========================
        // DELETE
        // ==========================
        if (isDeleted) {
          audit.push({
            object: objectType,
            objectId,
            eventType: this.translator("Audit-ChangeType-Delete"),
            objectDesignation: designation,
            property: "-",
            user: current[config.updateUserField ?? ""],
            changeDate: current[config.updateDateField ?? ""],
            oldValue: "-",
            newValue: null,
          });
          return;
        }

        // ==========================
        // UPDATE
        // ==========================
        const keysToCompare = Object.keys(current).filter(
          (k) => !ignored.has(k),
        );

        for (const key of keysToCompare) {
          let oldVal = previous[key];
          let newVal = current[key];

          // Handle reference fields (IdRef → Name)
          if (config.referenceFields?.[key]) {
            const nameField = config.referenceFields[key];
            oldVal = previous[nameField];
            newVal = current[nameField];
          }

          if (this.valuesAreDifferent(oldVal, newVal)) {
            audit.push({
              object: objectType,
              objectId,
              eventType: this.translator("Audit-ChangeType-Update"),
              objectDesignation: designation,
              property: config.fieldLabels?.[key] ?? key,
              user: current[config.updateUserField ?? ""],
              changeDate: current[config.updateDateField ?? ""],
              oldValue: this.normalizeValue(oldVal),
              newValue: this.normalizeValue(newVal),
            });
          }
        }
      });
    });

    return audit.reverse(); // newest first
  }

  // ============================================================
  // VALUE COMPARISON LOGIC
  // ============================================================

  private valuesAreDifferent(a: any, b: any): boolean {
    if (a == null && b == null) return false;
    if (a == null || b == null) return true;

    // Date-safe comparison
    if (this.isDate(a) && this.isDate(b)) {
      return new Date(a).getTime() !== new Date(b).getTime();
    }

    // Trim-safe string comparison
    if (typeof a === "string" && typeof b === "string") {
      return a.trim() !== b.trim();
    }

    return a !== b;
  }

  private isDate(value: any): boolean {
    return typeof value === "string" && !isNaN(Date.parse(value));
  }

  private normalizeValue(value: any): any {
    if (value == null) return null;

    if (this.isDate(value)) {
      return new Date(value).toLocaleString();
    }

    return value;
  }
}
